import { Injectable, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, map, takeUntil } from 'rxjs';
import { Visitor } from 'src/app/models/visitor';
import { IDashboardState } from 'src/app/state-management/states/aumye-dashboard.state';
import { IAumyeState } from 'src/app/state-management/states/aumye.state';

@Injectable({
  providedIn: 'root',
})
export class PreApprovedVisitorTimeService implements OnInit {
  private visitors: Visitor[] = [];
  private visitorTimers: Map<string, number> = new Map<string, number>(); // Map visitor ID to timer ID
  private visitorTimerSubjects: Map<string, BehaviorSubject<number>> = new Map<
    string,
    BehaviorSubject<number>
  >(); // Map visitor ID to timer subject
  constructor(private store: Store<IAumyeState>) {}
  dashboardState$: Observable<IDashboardState>;
  visitorData: Visitor[];
  destroyed$: Subject<any> = new Subject<any>();
  ngOnInit(): void {}

  fetchVisitorData() {
    const visitorTimers = localStorage.getItem('visitorTimers');
    if (visitorTimers != '{}') {
      this.dashboardState$ = this.store.select('aumyeDashboardState');
      this.dashboardState$
        .pipe(takeUntil(this.destroyed$))
        .subscribe((data) => {
          this.visitorData = data.visitorDetails.visitorList;
        });
    }
  }

  private loadTimersFromLocalStorage() {
    this.fetchVisitorData();
    const savedTimers = JSON.parse(
      localStorage.getItem('visitorTimers') || '{}'
    );
    for (const visitorID in savedTimers) {
      if (savedTimers.hasOwnProperty(visitorID)) {
        const remainingTime = savedTimers[visitorID];
        const visitor = this.getVisitorByID(visitorID);

        if (visitor) {
          this.visitorTimers.set(visitorID, remainingTime);

          if (!this.visitorTimerSubjects.has(visitorID)) {
            // Create a new BehaviorSubject if it doesn't exist
            this.visitorTimerSubjects.set(
              visitorID,
              new BehaviorSubject<number>(remainingTime)
            );
          }
          this.startTimer(visitor); // Resume timers
        } else {
          // Handle the case where the visitor data is not available
          // console.error(`Visitor data not found for ID: ${visitorID}`);
        }
      }
    }
  }

  private saveTimersToLocalStorage() {
    const timersToSave: { [visitorID: string]: number } = {};
    this.visitorTimers.forEach((remainingTime, visitorID) => {
      timersToSave[visitorID] = remainingTime;
    });
    localStorage.setItem('visitorTimers', JSON.stringify(timersToSave));
  }

  private getVisitorByID(visitorID: string): Visitor | undefined {
    return this.visitors.find((visitor) => visitor.visitorID === visitorID);
  }

  private onTimerExpiredAlert(visitor: Visitor): void {
    const IncidentManual = {
      IncidentType: 'Automatic',
      IncidentName: 'Uncheckout Visitor',
      IncidentLocation:
        visitor.visitorAddress?.length > 0
          ? visitor.visitorAddress
          : 'Host Address',
      IncidentStatus: 0,
      IncidentCriticality: 'L',
      AssignedTo: localStorage.getItem('FullName'),
      AssignedToUserId: localStorage.getItem('UserId'),
      EventCreatedBy: localStorage.getItem('FullName'),
      EventCreatedByUserID: localStorage.getItem('UserId'),
    };
    // this.store.dispatch(
    //   new fromDashboardActions.CreateManualIncident(IncidentManual)
    // );
  }
  checkme() {}

  setVisitorData(visitors: Visitor[]) {
    this.visitors = visitors;
    this.loadTimersFromLocalStorage();
  }
  getOnlyPendingVisitors(val: Visitor[]) {
    return val.filter(
      (index) =>
        index.isGateEntry == '1' &&
        new Date().getDate() == new Date(index.visitDateTime).getDate()
    );
  }
  startTimer(visitor: Visitor): void {
    this.fetchVisitorData();
    const visitorID = visitor.visitorID;
    const subject = this.visitorTimerSubjects.get(visitorID);

    if (!subject) {
      console.error(`Timer subject not found for visitorID: ${visitorID}`);
      return;
    }

    const interval = 1000; // 1 second interval
    let remainingTime = this.visitorTimers.get(visitorID) || 0; // Get remaining time from storage or initialize to 0

    if (remainingTime > 0) {
      // Resume the timer if there is remaining time
      const timerId = setInterval(() => {
        remainingTime = Math.max(remainingTime - 1, 0); // Decrease by 1 second
        this.visitorTimers.set(visitorID, remainingTime);
        subject.next(remainingTime);
        this.saveTimersToLocalStorage(); // Save the timer state
        visitor = this.visitorData.filter(
          (x) => x.visitorID === visitor.visitorID
        )[0];
        if (remainingTime === 0) {
          clearInterval(timerId);
          this.onTimerExpiredAlert(visitor);
        }
        if (visitor.checkOut != null) {
          clearInterval(timerId);
          this.checkme();
          this.removeFromLocalStorage(visitorID);
        }
      }, interval); // Update timer every 1000 ms (1 second)
    } else {
      // Check if the visitor ID exists in localStorage and the timer is zero (expired)
      const visitorTimersJSON = localStorage.getItem('visitorTimers');
      if (visitorTimersJSON) {
        const visitorTimers = JSON.parse(visitorTimersJSON);
        if (visitorTimers[visitorID] === 0) {
          // Visitor ID exists in localStorage, but the timer is zero (expired), so do nothing
          return;
        }
      }

      // Start a new timer with an initial time of 120 seconds
      remainingTime = 1800;
      const startTime = Date.now();

      const timerId = setInterval(() => {
        const currentTime = Date.now();
        remainingTime = Math.max(
          1800 - Math.floor((currentTime - startTime) / 1000),
          0
        );
        this.visitorTimers.set(visitorID, remainingTime);
        subject.next(remainingTime);
        this.saveTimersToLocalStorage(); // Save the timer state
        if (remainingTime === 0) {
          clearInterval(timerId);
          this.onTimerExpiredAlert(visitor);
        }
        visitor = this.visitorData.filter(
          (x) => x.visitorID === visitor.visitorID
        )[0];
        if (visitor.checkOut != null) {
          clearInterval(timerId);
          this.checkme();
          this.removeFromLocalStorage(visitorID);
        }
      }, interval); // Update timer every 1000 ms (1 second)
    }
  }

  getVisitorTimerObservable(visitor: Visitor): Observable<number> {
    if (!this.visitorTimerSubjects.get(visitor.visitorID)) {
      this.visitorTimerSubjects.set(
        visitor.visitorID,
        new BehaviorSubject<number>(0)
      );
    }
    return this.visitorTimerSubjects.get(visitor.visitorID).asObservable();
  }
  getMinutesRemaining(visitor: Visitor): Observable<number> {
    if (!this.visitorTimerSubjects.get(visitor.visitorID)) {
      this.visitorTimerSubjects.set(
        visitor.visitorID,
        new BehaviorSubject<number>(0)
      );
    }
    return this.visitorTimerSubjects
      .get(visitor.visitorID)
      .asObservable()
      .pipe(map((secondsRemaining) => Math.ceil(secondsRemaining / 60)));
  }
  getMinutesAndSecondsRemaining(visitor: Visitor): Observable<string> {
    if (!this.visitorTimerSubjects.has(visitor.visitorID)) {
      this.visitorTimerSubjects.set(
        visitor.visitorID,
        new BehaviorSubject<number>(0)
      );
    }
    return this.visitorTimerSubjects
      .get(visitor.visitorID)
      .asObservable()
      .pipe(
        map((secondsRemaining) => {
          const minutes = Math.floor(secondsRemaining / 60);
          const seconds = secondsRemaining % 60;
          const formattedTime = `${minutes}.${seconds.toFixed(0)}`;
          return formattedTime;
        })
      );
  }
  private stopTimer(visitor: Visitor): void {
    const visitorID = visitor.visitorID;
    const subject = this.visitorTimerSubjects.get(visitorID);

    if (subject) {
      subject.complete();
      this.visitorTimerSubjects.delete(visitorID);
      // You can also clear the timer here if needed
    }
  }
  private removeFromLocalStorage(visitorId: string): void {
    const timersToSave: { [visitorID: string]: number } = {};
    this.visitorTimers.forEach((remainingTime, visitorID) => {
      if (visitorID != visitorId) {
        timersToSave[visitorID] = remainingTime;
      } else {
        console.log('Checkout preapproved Visitor', visitorID);
      }
    });
    localStorage.removeItem('visitorTimers');
    localStorage.setItem('visitorTimers', JSON.stringify(timersToSave));
  }
}
